import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import { toTrimmedString } from 'static-solutions/24155/console-solution/lead-workbench/utils/workbench';

const getParticipantId = (participant) =>
	toTrimmedString(
		participant?.entityCode ||
			participant?.entityEmail ||
			participant?.userId ||
			participant?.userCode ||
			participant?.id ||
			participant?.participantId
	);

const getParticipantName = (participant) =>
	toTrimmedString(
		participant?.entityFullName ||
			participant?.name ||
			participant?.userName ||
			participant?.fullName ||
			participant?.displayName ||
			participant?.userFullName ||
			participant?.entityFirstName ||
			participant?.entityLastName
	);

const buildParticipantsMap = (participantsInfo, participantsList) => {
	if (participantsInfo && typeof participantsInfo === 'object' && !Array.isArray(participantsInfo)) {
		return participantsInfo;
	}

	const map = {};
	(participantsList || []).forEach((participant) => {
		const participantId = getParticipantId(participant);
		if (!participantId) return;
		const participantName = getParticipantName(participant) || participantId;
		map[participantId] = {
			entityCode: participantId,
			entityFullName: participantName,
			entityFirstName: participantName.split(' ')?.[0] || participantName,
		};
	});
	return map;
};

const buildParticipantsList = (participantsInfo, participantsList, userId) => {
	let ids = [];
	if (participantsInfo && typeof participantsInfo === 'object' && !Array.isArray(participantsInfo)) {
		ids = Object.keys(participantsInfo);
	} else if (Array.isArray(participantsList)) {
		ids = participantsList.map((participant) => getParticipantId(participant)).filter(Boolean);
	}

	const safeUserId = toTrimmedString(userId);
	if (safeUserId && !ids.includes(safeUserId)) ids.push(safeUserId);
	return ids;
};

const useIdentityCaseChatParticipants = ({ open, itemId, processCode, userId }) => {
	const [GetSingleIdentity, { isLoading }] = useGetSingleIdentityMutation();
	const [participantsMap, setParticipantsMap] = useState({});
	const [participantsList, setParticipantsList] = useState([]);

	const refreshParticipants = useCallback(async () => {
		const safeItemId = toTrimmedString(itemId);
		const safeProcessCode = toTrimmedString(processCode);
		if (!safeItemId || !safeProcessCode) return;

		const response = await GetSingleIdentity({
			_id: safeItemId,
			operation: 'get-single',
			processCode: safeProcessCode,
			suffixCode: '',
		}).unwrap();

		const participants = response?.lastCycleParticipants;
		const participantsInfo = participants?.participantsInfo || response?.participantsInfo;
		const participantsListRaw = Array.isArray(participants) ? participants : [];

		setParticipantsMap(buildParticipantsMap(participantsInfo, participantsListRaw));
		setParticipantsList(buildParticipantsList(participantsInfo, participantsListRaw, userId));
	}, [GetSingleIdentity, itemId, processCode, userId]);

	useEffect(() => {
		if (!open) return;
		let isActive = true;

		(async () => {
			try {
				await refreshParticipants();
			} catch {
				if (!isActive) return;
				setParticipantsMap({});
				setParticipantsList([]);
			}
		})();

		return () => {
			isActive = false;
		};
	}, [open, refreshParticipants]);

	return useMemo(
		() => ({
			participantsMap,
			participantsList,
			isParticipantsLoading: isLoading,
			refreshParticipants,
		}),
		[participantsMap, participantsList, isLoading, refreshParticipants]
	);
};

export default useIdentityCaseChatParticipants;
